import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Form, Button, Row, Col, Container } from 'react-bootstrap';
import axios from '../services/axiosConfig';
import { authService } from '../services/authService';
import { toast } from 'react-toastify';
import Select from 'react-select';
import '../styles/AddEditReportPage.css';
import LoadingIndicator from '../components/LoadingIndicator';
import { useSession } from '../context/SessionContext';

// API URL from environment variable
const API_URL = process.env.REACT_APP_API_URL;

/**
 * AddEditReportPage - Component for adding or editing reports
 */
const AddEditReportPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { reportId } = useParams();
  const isEditMode = !!reportId;
  const [menuOptions, setMenuOptions] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [filteredStateOptions, setFilteredStateOptions] = useState([]);
  const [selectedStateOptions, setSelectedStateOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const { userId, userRoles, isAdmin, userName, sessionData } = useSession();   
  
  // Get the userId from the main API response
  const mainApiUserId = sessionData?.userId || sessionData?.UserID || userId;
  
  // Define form state
  const [formData, setFormData] = useState({
    menuId: '',
    reportType: '',
    reportCode: '',
    reportName: '',
    reportDescription: '',
    reportPath: '',
    reportServer: '',
    reportProcedure: '',
    methodologyFile: '',
    methodologyPath: '',
    procedureServer: '',
    procedureDatabase: '',
    keywordsTags: '',
    enableStateRestriction: false,
    internalRole: '',
    mappedRoles: [],
    stateAccess: [],
    selectAllMappedRoles: true // Add selectAllMappedRoles to formData
  });
  
  // File upload reference
  const fileInputRef = React.createRef(); 
  // Load report data if in edit mode
  useEffect(() => {
        loadStateOptions();
    if (isEditMode) {
      const reportDetailsWrapper = location.state?.reportDetails;
      const reportDetails = reportDetailsWrapper?.reportDetails;

      if (reportDetails) {
        const methodologyPath = reportDetails.reportMethodologyPath || '';
        const methodologyFileName = methodologyPath.split('\\').pop(); // Corrected to handle single backslash
        console.log('Extracted Methodology Filename:', methodologyFileName);

        const matchedMenu = menuOptions.find(option => option.value === parseInt(reportDetails.menuId, 10));

        const matchedInternalRole = roleOptions.find(
          role => role.value === parseInt(reportDetailsWrapper?.rolesInfo?.internalRoleId, 10)
        );

        const updatedFormData = {
          menuId: matchedMenu || { value: reportDetails.menuId, label: `Menu ${reportDetails.menuId}` },
          reportType: reportDetails.reportVersion || '',
          reportCode: reportDetails.reportCode || '',
          reportName: reportDetails.reportName || '',
          reportDescription: reportDetails.reportDescription || '',
          reportPath: reportDetails.reportPath || '',
          reportServer: reportDetails.reportServer || '',
          reportProcedure: reportDetails.reportProcedureName || '',
          methodologyPath: methodologyPath,
          methodologyFile: methodologyFileName,
          procedureServer: reportDetails.reportProcedureServer || '',
          procedureDatabase: reportDetails.reportProcedureDatabase || '',
          keywordsTags: reportDetails.keywordsAndTags || '',
          enableStateRestriction: reportDetails.enableStateRestriction || false,
          mappedRoles: reportDetailsWrapper?.rolesInfo?.externalRoles?.map(role => ({ value: parseInt(role.roleId, 10), label: role.roleName })) || [],
          internalRole: matchedInternalRole || '',
          stateAccess: reportDetailsWrapper?.stateRestrictions?.map(state => ({
            value: parseInt(state.restrictionId, 10),
            checked: true, // Ensure presented IDs are checked
          })) || [],
          selectAllMappedRoles: reportDetailsWrapper?.rolesInfo?.externalRoles?.length > 0 || false, // Initialize based on external roles
        };

        setFormData(updatedFormData);
        console.log('Updated formData stateAccess:', updatedFormData.stateAccess); // Log stateAccess after updating
        setSelectedStateOptions(
          reportDetailsWrapper?.stateRestrictions?.map(state => ({
            value: parseInt(state.restrictionId, 10),
           
            checked: true, // Ensure presented IDs are checked
          })) || []
        );
        setFilteredStateOptions(
          reportDetailsWrapper?.stateRestrictions?.map(state => ({
            value: parseInt(state.restrictionId, 10),
            
            checked: true, // Ensure presented IDs are checked
          })) || []
        );

        console.log('Updated formData:', updatedFormData);
      } else {
        console.error('Report details not found in location.state');
        // setErrorMessage('Failed to load report details');
      }
    } else {
      // Check if we have menu ID from the previous page
      const params = new URLSearchParams(location.search);
      const menuId = params.get('menuId');
      if (menuId) {
        const matchedMenu = menuOptions.find(option => option.value === parseInt(menuId, 10));
        setFormData(prev => ({ ...prev, menuId: matchedMenu || { value: menuId, label: `Menu ${menuId}` } }));
      }
    }
  }, [isEditMode, location.state, menuOptions, roleOptions]);

  // console.log('Rendering Methodology File: just after setting form data ', formData.methodologyFile);
  
  // Get token from cookie directly
  // const getTokenFromCookie = () => {
  //   const value = `; ${document.cookie}`;
  //   const parts = value.split(`; auth_token=`);
  //   if (parts.length === 2) return parts.pop().split(';').shift();
  //   return null;
  // };
  
  // Load state options
  const loadStateOptions = async () => {
    if ( !formData.selectAllMappedRoles) {
      console.log('State API not called: internalRole or selectAllMappedRoles condition not met');
      return; // Do not call the API if conditions are not met
    }

    try {
      setLoading(true);
      console.log('Calling State API with userId from main API response:', mainApiUserId);
      
      // Try to get token from cookie first
      // let token = getTokenFromCookie();
      // // Fall back to authService if cookie token is not available
      // if (!token) {
      //   token = await authService.acquireTokenForAPI();
      // }
      
      const response = await axios.get(`${API_URL}/AddEditReport/GetStates`, {
        params: { UserID: mainApiUserId },
        // headers: { 'Authorization': `Bearer ${token}` }
      });
      console.log('Raw State API Response:', response); // Log the raw response

      if (!response.data || !response.data.states) {
        console.error('State API response does not contain expected data structure');
        setErrorMessage('State API returned invalid data');
        return;
      }

      const states = response.data.states;
      // console.log('Extracted States:', states); // Log the extracted states

      const options = states.map(state => ({
        value: state.stateId,
        label: state.stateCode
      }));

      // console.log('Mapped State Options:', options); // Log the mapped state options

      setStateOptions(options);
      setFilteredStateOptions(options);
    } catch (error) {
      console.error('Error loading state options:', error);
      // setErrorMessage('Failed to load state options');
    } finally {
      setLoading(false);
    }
  };
  
  // Filter state options based on search input
  const filterStateOptions = (searchText) => {
    setStateFilter(searchText);
    if (!searchText) {
      setFilteredStateOptions(stateOptions.map(state => ({
        ...state,
        checked: selectedStateOptions.some(selected => selected.value === state.value),
      })));
      return;
    }

    const filterStatesRecursively = (states, searchTerm) => {
      return states
        .filter(state => {
          const matches = state.label.toLowerCase().includes(searchTerm.toLowerCase());
          const matchingChildren = state.children
            ? filterStatesRecursively(state.children, searchTerm)
            : [];
          return matches || matchingChildren.length > 0;
        })
        .map(state => ({
          ...state,
          children: state.children ? filterStatesRecursively(state.children, searchTerm) : [],
          checked: selectedStateOptions.some(selected => selected.value === state.value),
        }));
    };

    const filtered = filterStatesRecursively(stateOptions, searchText);
    setFilteredStateOptions(filtered);
  };
  
  // Handle form field changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  
  // Update logic for handling mapped roles
  const loadMappedRoles = async (internalRoleId) => {
    try {
      console.log(`Fetching mapped roles for internalRoleId: ${internalRoleId}`);
      
      // Try to get token from cookie first
      // let token = getTokenFromCookie();
      // // Fall back to authService if cookie token is not available
      // if (!token) {
      //   token = await authService.acquireTokenForAPI();
      // }
      
      const response = await axios.get(`${API_URL}/ManageRoles/GetMappedRoles`, {
        params: { internalRoleId },
        // headers: { 'Authorization': `Bearer ${token}` }
      });

      console.log('API Response:', response.data); // Log the API response

      const mappedRoles = response.data.mappedExternalRoles.map(role => {
        console.log(`Mapped Role - Value: ${role.externalRoleId}, Label: ${role.externalRoleName}`);
        return {
          value: role.externalRoleId,
          label: role.externalRoleName,
          selected: role.checked || false, // Ensure the `selected` property reflects the API response
        };
      });

      console.log('Mapped roles fetched successfully:', mappedRoles);
      setFormData(prev => ({
        ...prev,
        mappedRoles,
      }));
    } catch (error) {
      console.error('Error fetching mapped roles:', error);
      console.error('Error details:', error.response?.data || 'No additional error details'); // Log error details
      //setErrorMessage('Failed to load mapped roles');
    }
  };

  // Ensure roles are pre-checked based on the initial state
  const renderMappedRolesCheckboxes = () => {
    if (!formData.mappedRoles || formData.mappedRoles.length === 0) {
      return <p className="text-muted">No mapped roles available</p>;
    }

    return (
      <div className="mapped-roles-container">
        {formData.mappedRoles.map(role => (
          <div key={role.value} className="state-item">
            <input
              type="checkbox"
              id={`mapped-role-${role.value}`}
              checked={formData.selectedMappedRoles?.includes(role.value) || true} // Ensure roles are pre-checked
              onChange={() => handleMappedRoleSelect(role.value)} // Add onChange handler
            />
            <label htmlFor={`mapped-role-${role.value}`}>{role.label || 'Unnamed Role'}</label>
          </div>
        ))}
      </div>
    );
  };

  // Updated handleSelectChange function
  const handleSelectChange = (selectedOption, { name }) => {
    console.log(`handleSelectChange triggered for field: ${name}`); // Log the field name
    console.log(`Selected option in ${name}:`, selectedOption); // Log the selected option

    setFormData(prev => ({
      ...prev,
      [name]: selectedOption,
      ...(name === 'internalRole' && {
        selectMappedRole: selectedOption ? true : false, // Initialize selectMappedRole
        selectAllMappedRoles: selectedOption ? true : false, // Initialize selectAllMappedRoles
        mappedRoles: [], // Clear mapped roles initially
      }),
    }));

    // If internal role selection changes, load mapped external roles
    if (name === 'internalRole' && selectedOption) {
        loadMappedRoles(selectedOption.value); // Fetch mapped roles for the selected internal role
    }
  };
  
  // Handle file selection for methodology document
  const handleFileChange = (e) => {
    setFormData(prev => ({
      ...prev,
      methodologyFile: e.target.files[0]
    }));
  };
  
  // Handle state selection
  const handleStateSelect = (state) => {
    const isSelected = selectedStateOptions.some(s => s.value === state.value);
    
    if (isSelected) {
      setSelectedStateOptions(selectedStateOptions.filter(s => s.value !== state.value));
    } else {
      setSelectedStateOptions([...selectedStateOptions, state]);
    }
  };
  
  // Handle mapped role selection
  const handleMappedRoleSelect = (roleId) => {
    setFormData(prev => {
      const isSelected = prev.selectedMappedRoles?.includes(roleId);
      const updatedSelectedRoles = isSelected
        ? prev.selectedMappedRoles.filter(id => id !== roleId)
        : [...(prev.selectedMappedRoles || []), roleId];

      return {
        ...prev,
        selectedMappedRoles: updatedSelectedRoles,
      };
    });
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Common validation for both add and edit
    if (!formData.menuId) {
      setErrorMessage('Please select a menu');
      return;
    }
    
    if (!formData.reportType) {
      setErrorMessage('Please select a report type');
      return;
    }

    if (!formData.reportCode || formData.reportCode.trim() === '') {
      setErrorMessage('Please enter a report code');
      return;
    }

    if (!formData.reportName || formData.reportName.trim() === '') {
      setErrorMessage('Please enter a report name');
      return;
    }

    if (!formData.reportDescription || formData.reportDescription.trim() === '') {
      setErrorMessage('Please enter a report description');
      return;
    }

    // Report path and server are always required, regardless of report type
    if (!formData.reportServer || formData.reportServer.trim() === '') {
      setErrorMessage('Please enter a report server');
      return;
    }

    if (!formData.reportPath || formData.reportPath.trim() === '') {
      setErrorMessage('Please enter a report path');
      return;
    }
    
    // Additional validation logic as needed
    
    try {
      // Clear any previous error messages
      setErrorMessage('');
      setLoading(true);
      
      console.log('Form submitted, determining mode...');
      if (isEditMode) {
        await handleEditReport();
      } else {
        await handleAddReport();
      }
    } catch (error) {
      console.error('Error saving report:', error);
      setErrorMessage(error.response?.data?.message || 'Failed to save report');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle cancel button click
  const handleCancel = () => {
    navigate('/manage-reports');
  };
  
 
  // Fetch menus from API (similar to ManageReportsPage)
  const fetchMenus = async () => {
    try {
      setLoading(true);

      // Try to get token from cookie first
      // let token = getTokenFromCookie();
      // // Fall back to authService if cookie token is not available
      // if (!token) {
      //   token = await authService.acquireTokenForAPI();
      // }
      
      const response = await axios.get(`${API_URL}/GetMenuList`, {
        // headers: { 'Authorization': `Bearer ${token}` }
      });      if (response.data.success && response.data.allMenus) {
        // Filter menus with parentMenuId === 0 and exclude "Admin" menu
        const filteredMenus = response.data.allMenus.filter(
          (menu) => menu.parentMenuId === 0 && menu.menuName !== "Admin"
        );

        const options = filteredMenus.map(menu => ({
          value: menu.menuId,
          label: menu.menuName
        }));

        setMenuOptions(options);
      } else {
        setMenuOptions([]);
        setErrorMessage('No menus found');
      }
    } catch (error) {
      console.error('Error fetching menus:', error);
      setErrorMessage('Failed to load menu data');
    } finally {
      setLoading(false);
    }
  };
 // Call loadInternalRoles on component mount
  // Call fetchMenus on component mount
  useEffect(() => {
    fetchMenus();
     loadInternalRoles();
  }, []);
  
  // Pre-select menu ID from URL parameter
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const menuId = params.get('menuId');
    if (menuId) {
      setFormData(prev => ({ ...prev, menuId: { value: menuId, label: menuOptions.find(option => option.value === parseInt(menuId))?.label || '' } }));
    }
  }, [menuOptions]);
  
  // Load internal roles from API
  const loadInternalRoles = async () => {
    try {
      setLoading(true);
      
      // Try to get token from cookie first
      // let token = getTokenFromCookie();
      // // Fall back to authService if cookie token is not available
      // if (!token) {
      //   token = await authService.acquireTokenForAPI();
      // }
      
      const response = await axios.get(`${API_URL}/ConfigExternalRoles/LoadInternalRoles`, {
        // headers: { 'Authorization': `Bearer ${token}` }
      });
      const roles = response.data.internalRoles;

      // Transform data for react-select
      const options = roles.map(role => ({
        value: role.id,
        label: role.roleName
      }));

      setRoleOptions(options);
    } catch (error) {
      console.error('Error loading internal roles:', error);
      setErrorMessage('Failed to load internal roles');
    } finally {
      setLoading(false);
    }
  };

 
  
  // Handle Add Report API call
  const handleAddReport = async () => {
    console.log('Save button clicked, initiating Add Report process...');
    try {
      setLoading(true);

      // Prepare payload for API
      const payload = {
        reportName: formData.reportName,
        reportPath: formData.reportPath,
        reportCode: formData.reportCode,
        reportDescription: formData.reportDescription,
        reportServer: formData.reportServer,
        menuId: formData.menuId.value, // Ensure menuId is passed correctly
        methodologyPath: '', // Placeholder for methodology path
        methodologyFile: '', // Placeholder for methodology file name
        reportVersion: formData.reportType,
        reportProcedureName: formData.reportProcedure,
        reportProcedureServer: formData.procedureServer,
        reportProcedureDatabase: formData.procedureDatabase,
        userName: userName, // Replace with actual user context
        userID: userId,
        keywordsTags: formData.keywordsTags || '',
        enableStateRestriction: formData.enableStateRestriction,
        externalRoleMappings: formData.internalRole ? [{
          roleId: formData.internalRole.value, // Use the selected internal role
          isSelected: true,
        }] : [],
        stateRestrictions: selectedStateOptions.map(state => ({
          stateId: state.value,
        })),
      };

      console.log('Payload prepared for Add Report API:', payload); // Log the payload

      // Get token from cookie first, then fall back to authService
      // let token = getTokenFromCookie();
      // if (!token) {
      //   token = await authService.acquireTokenForAPI();
      // }
      
      const response = await axios.post(
        `${API_URL}/AddEditReport/AddReport`,
        payload,
        {
          // headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      // Handle success
      console.log('Report added successfully, response:', response);
      setSuccessMessage('Report added successfully!');
      toast.success('Report added successfully!');

      // Upload methodology file after report is added
      const reportId = response.data.reportId; // Assuming the response contains the report ID
      if (formData.methodologyFile) {
        await uploadMethodologyFile(reportId);
      }

      navigate('/manage-reports');
    } catch (error) {
      console.error('Error adding report:', error);
      setErrorMessage(error.response?.data?.message || 'Failed to add report');
      toast.error(error.response?.data?.message || 'Failed to add report');
    } finally {
      setLoading(false);
    }
  };

  // Handle Edit Report API call
  const handleEditReport = async () => {
    console.log('Save button clicked, initiating Edit Report process...');
    try {
      // Double-check validation for mandatory fields before submitting
      if (!formData.reportName || !formData.reportPath || !formData.reportCode || 
          !formData.reportDescription || !formData.reportServer) {
        setErrorMessage('Please fill in all mandatory fields');
        setLoading(false);
        return;
      }
      
      setLoading(true);

      // Prepare payload for API
      const payload = {
        reportName: formData.reportName,
        reportPath: formData.reportPath,
        reportCode: formData.reportCode,
        reportDescription: formData.reportDescription,
        reportServer: formData.reportServer,
        menuId: formData.menuId.value,
        methodologyPath: formData.methodologyPath || '',
        methodologyFile:'',
        reportVersion: formData.reportType,
        reportProcedureName: formData.reportProcedure,
        reportProcedureServer: formData.procedureServer,
        reportProcedureDatabase: formData.procedureDatabase,
        userName: userName,
        keywordsTags: formData.keywordsTags || '',
        enableStateRestriction: formData.enableStateRestriction,
        externalRoleMappings: formData.mappedRoles.map(role => ({
          roleId: role.value,
          isSelected: true,
        })),
        stateRestrictions: selectedStateOptions.map(state => ({
          stateId: state.value,
        })),
        reportId: reportId, // Include reportId for edit mode
        userID: userId
      };

      console.log('Payload prepared for Edit Report API:', payload); // Log the payload

      // Get token from cookie first, then fall back to authService
      // let token = getTokenFromCookie();
      // if (!token) {
      //   token = await authService.acquireTokenForAPI();
      // }
      
      const response = await axios.post(
        `${API_URL}/AddEditReport/EditReport`,
        payload,
        {
          // headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      // Handle success
      console.log('Report edited successfully, response:', response);
      setSuccessMessage('Report edited successfully!');
      toast.success('Report edited successfully!');

      // Upload methodology file after report is edited
      if (formData.methodologyFile) {
        await uploadMethodologyFile(reportId);
      }

      // Add a short delay before navigating
      setTimeout(() => {
        navigate('/manage-reports');
      }, 1500);
    } catch (error) {
      console.error('Error editing report:', error);
      setErrorMessage(error.response?.data?.message || 'Failed to edit report');
      toast.error(error.response?.data?.message || 'Failed to edit report');
    } finally {
      setLoading(false);
    }
  };

  // Refine the uploadMethodologyFile function to ensure proper file upload
  const uploadMethodologyFile = async (reportId, tokenParam) => {
    try {
      if (!formData.methodologyFile) {
        console.log('No methodology file selected, skipping upload');
        return;
      }

      const formDataToUpload = new FormData();
      formDataToUpload.append('File', formData.methodologyFile); // Match backend key
      formDataToUpload.append('ReportId', reportId.toString()); // Ensure ReportId is a string

      console.log('Uploading methodology file with report ID:', reportId);

      // Use provided token or get from cookie, then fall back to authService
      // let token = tokenParam;
      // if (!token) {
      //   token = getTokenFromCookie();
      //   if (!token) {
      //     token = await authService.acquireTokenForAPI();
      //   }
      // }
      
      const response = await axios.post(
        `${API_URL}/AddEditReport/UploadMethodology`,
        formDataToUpload,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            // 'Authorization': `Bearer ${token}`
          },
        }
      );

      console.log('Methodology file uploaded successfully:', response.data);
      toast.success(response.data.message || 'Methodology file uploaded successfully!');
    } catch (error) {
      console.error('Error uploading methodology file:', error);
      const errorDetails = error.response?.data || 'Unknown error';
      setErrorMessage(errorDetails.message || 'Failed to upload methodology file');
      toast.error(errorDetails.message || 'Failed to upload methodology file');
    }
  };

  // Function to handle downloading the methodology file
  const handleDownloadMethodology = async () => {
    if (!formData.methodologyPath) {
      setErrorMessage('No methodology file available to download.');
      return;
    }

    try {
      setLoading(true);
      
      // Try to get token from cookie first
      // let token = getTokenFromCookie();
      // // Fall back to authService if cookie token is not available
      // if (!token) {
      //   token = await authService.acquireTokenForAPI();
      // }
      
      const response = await axios.get(
        `${API_URL}/AddEditReport/DownloadMethodology`,
        {
          params: { filePath: formData.methodologyPath },
          responseType: 'blob',
          // headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      // Create a link to download the file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', formData.methodologyPath.split('/').pop());
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Methodology file downloaded successfully!');
    } catch (error) {
      console.error('Error downloading methodology file:', error);
      setErrorMessage('Failed to download methodology file.');
    } finally {
      setLoading(false);
    }
  };

  // Function to handle deleting the methodology file
  const handleDeleteMethodology = async () => {
    if (!formData.methodologyPath || !formData.methodologyFile) {
      setErrorMessage('No methodology file available to delete.');
      return;
    }

    try {
      setLoading(true);

      const requestBody = {
        filePath: formData.methodologyPath, // Include filePath in the payload
        reportId: reportId.toString(), // Ensure reportId is a string
        fileName: formData.methodologyFile, // Ensure fileName is passed correctly
      };

      // Try to get token from cookie first
      // let token = getTokenFromCookie();
      // // Fall back to authService if cookie token is not available
      // if (!token) {
      //   token = await authService.acquireTokenForAPI();
      // }
      
      const response = await axios.delete(
        `${API_URL}/AddEditReport/DeleteMethodology`,
        {
          data: requestBody,
          // headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      toast.success(response.data.message || 'Methodology file deleted successfully!');

      // Clear methodology file and path from form data
      setFormData(prev => ({ ...prev, methodologyPath: '', methodologyFile: null }));
    } catch (error) {
      console.error('Error deleting methodology file:', error);
      setErrorMessage(error.response?.data?.message || 'Failed to delete methodology file.');
    } finally {
      setLoading(false);
    }
  };

  // Add a new function to handle file upload directly when the "Upload" button is clicked
  const handleUploadButtonClick = async () => {
    if (!formData.methodologyFile) {
      setErrorMessage('Please select a file to upload.');
      return;
    }

    try {
      setLoading(true);

      const formDataToUpload = new FormData();
      formDataToUpload.append('File', formData.methodologyFile); // Match backend key
      formDataToUpload.append('ReportId', reportId.toString()); // Ensure ReportId is a string

      console.log('Uploading methodology file with report ID:', reportId);

      // Try to get token from cookie first
     // let token = getTokenFromCookie();
      // Fall back to authService if cookie token is not available
      // if (!token) {
      //   token = await authService.acquireTokenForAPI();
      // }

      const response = await axios.post(
        `${API_URL}/AddEditReport/UploadMethodology`,
        formDataToUpload,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            // 'Authorization': `Bearer ${token}`
          },
        }
      );

      console.log('Methodology file uploaded successfully:', response.data);
      toast.success(response.data.message || 'Methodology file uploaded successfully!');

      // Update formData with the uploaded file's path
      setFormData(prev => ({
        ...prev,
        methodologyPath: response.data.filePath || '',
      }));
    } catch (error) {
      console.error('Error uploading methodology file:', error);
      const errorDetails = error.response?.data || 'Unknown error';
      setErrorMessage(errorDetails.message || 'Failed to upload methodology file');
      toast.error(errorDetails.message || 'Failed to upload methodology file');
    } finally {
      setLoading(false);
    }
  };

  // Update the logic to check state values based on state IDs from `getEditReportDetails`
  const loadEditReportDetails = async () => {
    try {
        const reportDetailsWrapper = location.state?.reportDetails;
        const stateIds = reportDetailsWrapper?.stateRestrictions?.map(state => state.restrictionId) || [];

        console.log('Retrieved stateIds:', stateIds); // Log stateIds to verify

        // Fetch state access options
        // Try to get token from cookie first
        // let token = getTokenFromCookie();
        // // Fall back to authService if cookie token is not available
        // if (!token) {
        //   token = await authService.acquireTokenForAPI();
        // }
        
        console.log('Fetching states with userId from main API response:', mainApiUserId);
        
        const response = await axios.get(`${API_URL}/AddEditReport/GetStates`, {
         params: { UserID: mainApiUserId },
        //  headers: { 'Authorization': `Bearer ${token}` }
       });
        const states = response.data.states;

        // Mark states as checked based on state IDs
        const stateOptions = states.map(state => ({
            value: state.stateId,
            label: state.stateCode,
            checked: stateIds.includes(state.stateId),
        }));

        console.log('State options after processing:', stateOptions); // Log state options to verify

        // Update state only if necessary
        if (JSON.stringify(stateOptions) !== JSON.stringify(formData.stateAccess)) {
            console.log('Updating stateAccess and selectedStateOptions');
            setFormData(prev => ({
                ...prev,
                stateAccess: stateOptions,
            }));

            setSelectedStateOptions(stateOptions.filter(state => state.checked));
            setFilteredStateOptions(stateOptions);

            console.log('Updated stateAccess:', stateOptions);
            console.log('Updated selectedStateOptions:', stateOptions.filter(state => state.checked));
            console.log('Updated filteredStateOptions:', stateOptions);
        } else {
            console.log('No changes detected in state options, skipping update');
        }
    } catch (error) {
        console.error('Error loading edit report details:', error);
    }
  };


  // Call `loadEditReportDetails` in `useEffect` when in edit mode
  let isLoadEditReportDetailsCalled = false;
  useEffect(() => {
    if (isEditMode && !isLoadEditReportDetailsCalled) {
      console.log('Edit mode detected, calling loadEditReportDetails');
      loadEditReportDetails();
      isLoadEditReportDetailsCalled = true;
    }
  }, [isEditMode]);

  // Call loadStateOptions in create mode when internalRole and selectAllMappedRoles are set
  useEffect(() => {
    if (!isEditMode && formData.internalRole && formData.selectAllMappedRoles) {
      loadStateOptions();
    }
  }, [isEditMode, formData.internalRole, formData.selectAllMappedRoles]);

  return (
    <div className="addedit-report-page">
      {loading && <LoadingIndicator />}
      <div>
      {/* Update the page header text based on the mode */}
      <div className="page-header-bar">
        {isEditMode ? 'Edit Report' : 'Create Report'}
      </div>
      
      <Container fluid>
        {errorMessage && (
          <div className="alert alert-danger" role="alert">
            {errorMessage}
          </div>
        )}
        
        {successMessage && (
          <div className="alert alert-success" role="alert">
            {successMessage}
          </div>
        )}
        
        <Form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group col-md-2">
              <label htmlFor="menuId" className="form-label">
                Select Menu
                <span className="asterisk">*</span>
              </label>
            </div>
            <div className="col-md-10">
              <Select
                name="menuId"
                value={formData.menuId}
                onChange={(option) => handleSelectChange(option, { name: 'menuId' })}
                options={menuOptions}
                isDisabled={loading}
                placeholder="Select Menu"
                className="basic-select"
                classNamePrefix="select"
              />
            </div>
          </div>

          {/* Adjust layout to align "Report Type" with "Select Management" and "Report Name" in the same line */}
          <div className="form-row">
            <div className="form-group col-md-2">
              <label htmlFor="reportType" className="form-label">
                Report Type
                <span className="asterisk">*</span>
              </label>
            </div>
            <div className="col-md-4">
              <Form.Select 
                id="reportType"
                name="reportType"
                value={formData.reportType}
                onChange={handleChange}
                disabled={loading}
              >
                <option value="">Select</option>
                <option value="RedirectType">Redirect Type</option>
                <option value="2008">SSRS 2008</option>
                <option value="2014">SSRS 2014-2016</option>
              </Form.Select>
            </div>
            <div className="col-md-2">
                    <div className="form-label-group">
                     <label className="form-label">
                        Report Code <span className="required-indicator">*</span>
                      </label>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <Form.Control
                      type="text"
                       name="reportCode"
                       value={formData.reportCode}
                       onChange={handleChange}
                      disabled={isEditMode || loading}
                       required
                     />
                  </div>
           
          </div>
          
          <div className="form-row">
            <div className="form-group col-md-2">
              <label htmlFor="reportDescription" className="form-label">
                Report Description
                <span className="asterisk">*</span>
              </label>
            </div>
            <div className="col-md-4">
              <Form.Control
                id="reportDescription"
                as="textarea"
                rows={3}
                name="reportDescription"
                value={formData.reportDescription}
                onChange={handleChange}
                disabled={loading}
                required
              />
            </div>
            <div className="form-group col-md-2">
              <label htmlFor="reportName" className="form-label">
                Report Name
                <span className="asterisk">*</span>
              </label>
            </div>
            <div className="col-md-4">
              <Form.Control
                id="reportName"
                type="text"
                name="reportName"
                value={formData.reportName}
                onChange={handleChange}
                disabled={loading}
                required
              />
            </div>
          </div>
          
          {/* Add "Report Path" to the "Report Server" row */}
          {(!formData.reportType || formData.reportType === '2008' || formData.reportType === '2014') && (
            <div className="form-row">
              <div className="form-group col-md-2">
                <label htmlFor="reportServer" className="form-label">
                  Report Server
                  <span className="asterisk">*</span>
                </label>
              </div>
              <div className="col-md-4">
                <Form.Control
                  id="reportServer"
                  as="textarea"
                  rows={3}
                  name="reportServer"
                  value={formData.reportServer}
                  onChange={handleChange}
                  disabled={loading}
                  required
                />
              </div>
              <div className="form-group col-md-2">
                <label htmlFor="reportPath" className="form-label">
                  Report Path
                  <span className="asterisk">*</span>
                </label>
              </div>
              <div className="col-md-4">
                <Form.Control
                  id="reportPath"
                  as="textarea"
                  rows={3}
                  name="reportPath"
                  value={formData.reportPath}
                  onChange={handleChange}
                  disabled={loading}
                  required
                />
              </div>
            </div>
          )}
          
          {/* Adjust layout to display "Methodology" and "Report Procedure" in one row */}
          <div className="form-row">
            <div className="form-group col-md-2">
              <label htmlFor="methodologyFile" className="form-label">
                Methodology
              </label>
            </div>
            <div className="col-md-4">
              <Form.Control
                id="methodologyFile"
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                disabled={loading}
              />
             
               {/* Add 'Download,' 'Upload,' and 'Delete' buttons below the 'Methodology' section in the UI when in edit mode */}
        {isEditMode && (
          <div className="mt-2">
            {formData.methodologyPath && (
              <Button
               variant="secondary"
                className="me-2"
                onClick={handleDownloadMethodology}
                disabled={loading}
                title={formData.methodologyFile} // Set the tooltip to show the file name
                
              >
                Download
              </Button>
            )}
            <Button
              variant="secondary"
              className="me-2"
              onClick={handleUploadButtonClick}
              disabled={loading}
            >
              Upload
            </Button>
            {formData.methodologyPath && (
              <Button
                   variant="secondary"
                onClick={handleDeleteMethodology}
                disabled={loading}
              >
                Delete
              </Button>
            )}
          </div>
        )}
      
      
            </div>

            <div className="form-group col-md-2">
              <label htmlFor="reportProcedure" className="form-label">
                Report Procedure
              </label>
            </div>
            <div className="col-md-4">
              <Form.Control
                id="reportProcedure"
                type="text"
                name="reportProcedure"
                value={formData.reportProcedure}
                onChange={handleChange}
                disabled={loading}
              />
            </div>
          </div>
          
          {/* Adjust layout to display "Procedure Server" and "Procedure Database" in another row */}
          <div className="form-row">
            <div className="form-group col-md-2">
              <label htmlFor="procedureServer" className="form-label">
                Procedure Server
              </label>
            </div>
            <div className="col-md-4">
              <Form.Control
                id="procedureServer"
                type="text"
                name="procedureServer"
                value={formData.procedureServer}
                onChange={handleChange}
                disabled={loading}
              />
            </div>
            <div className="form-group col-md-2">
              <label htmlFor="procedureDatabase" className="form-label" style={{ whiteSpace: 'nowrap' }}>
                Procedure Database
              </label>
            </div>
            <div className="col-md-4">
              <Form.Control
                id="procedureDatabase"
                type="text"
                name="procedureDatabase"
                value={formData.procedureDatabase}
                onChange={handleChange}
                disabled={loading}
              />
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group col-md-2">
              <label htmlFor="keywordsTags" className="form-label">
                Keywords & Tags
              </label>
            </div>
            <div className="col-md-4">
              <Form.Control
                id="keywordsTags"
                as="textarea"
                rows={3}
                name="keywordsTags"
                value={formData.keywordsTags}
                onChange={handleChange}
                disabled={loading}
              />
            </div>
           
  <div className="form-group col-md-2 d-flex align-items-center">
    <label htmlFor="enableStateRestriction" className="form-label me-2">
      Enable State Restriction
    </label>
    <Form.Check
      id="enableStateRestriction"
      name="enableStateRestriction"
      checked={formData.enableStateRestriction}
      onChange={handleChange}
      disabled={loading}
      className="form-check-inline"
    />
  </div>

          </div>
          
         
  <div className="form-row">
  <div className="form-group col-md-2">
    <label htmlFor="internalRole" className="form-label">
      Select Internal Roles
    </label>
  </div>
  <div className="form-group col-md-4"> {/* Adjusted column width */}
    <Select
      id="internalRole"
      name="internalRole"
      value={formData.internalRole}
      onChange={(option) => handleSelectChange(option, { name: 'internalRole' })}
      options={roleOptions}
      isDisabled={loading}
      placeholder="Select"
      className="basic-select"
      classNamePrefix="select"
      styles={{
        container: (provided) => ({
          ...provided,
          width: '100%', // Adjust width to fit the container
        }),
        control: (provided) => ({
          ...provided,
          minWidth: '300px', // Set minimum width for the dropdown
        }),
      }}
    />
  </div>
</div>
          
          {/* Combine "Select Mapped Roles" and "Select State Access" into a single row */}
<div className="form-row">
  <div className="form-group col-md-2">
    <label className="form-label">
      Select Mapped Roles
    </label>
    </div>
    <div className="col-md-4">
      {formData.internalRole && (
        <div className="state-list-container">
          <div className="state-item">
            <input
              type="checkbox"
              id="select-all-mapped-roles"
              checked={formData.selectAllMappedRoles}
              onChange={(e) => setFormData(prev => ({ ...prev, selectAllMappedRoles: e.target.checked }))}
            />
            <label htmlFor="select-all-mapped-roles">Select All</label>
          </div>
          {renderMappedRolesCheckboxes()} {/* Render role names below "Select All" checkbox */}
        </div>
      )}
    </div>
 
  <div className="form-group col-md-2">
    <label htmlFor="stateAccess" className="form-label">
      Select State Access
    </label>
  </div>
  <div className="col-md-4">
    <div style={{ position: 'relative', width: '100%' }}> {/* Added relative positioning */}
      <input
        type="text"
        placeholder="Filter State Access..."
        value={stateFilter}
        onChange={(e) => {
          setStateFilter(e.target.value);
          filterStateOptions(e.target.value);
        }}
        disabled={!formData.internalRole || loading} // Disabled if no internal role is selected
        className="form-control"
      />
      {/* {!formData.internalRole && (
        <p className="text-muted" style={{ marginTop: '10px' }}>Select an internal role to view state access options</p>
      )} */}
      <div
        className="state-list-container"
        style={{
          maxHeight: '300px',
          overflowY: 'auto',
          marginTop: '10px',
          backgroundColor: '#fff', // Add background for better visibility
          border: '1px solid #ccc', // Add border for styling
          width: '100%', // Ensure it matches the input width
        }}
      >
        <>
            <div className="state-item">
              <input
                type="checkbox"
                id="select-all-states"
                checked={selectedStateOptions.length === filteredStateOptions.length}
                onChange={() => {
                  if (selectedStateOptions.length === filteredStateOptions.length) {
                    setSelectedStateOptions([]); // Deselect all
                  } else {
                    setSelectedStateOptions(filteredStateOptions); // Select all
                  }
                }}
                disabled={loading} // Disabled if loading
              />
              <label htmlFor="select-all-states">Select All</label>
            </div>
            {filteredStateOptions.length > 0 ? (
              filteredStateOptions.map(state => (
                <div key={state.value} className="state-item">
                  <input
                    type="checkbox"
                    id={`state-${state.value}`}
                    checked={selectedStateOptions.some(s => s.value === state.value)}
                    onChange={() => handleStateSelect(state)}
                    disabled={loading} // Ensure checkboxes are only disabled during loading
                  />
                  <label htmlFor={`state-${state.value}`}>{state.label}</label>
                </div>
              ))
            ) : (
              <p className="text-muted">No states match your filter</p>
            )}
          </>
      </div>
    </div>
  </div>
</div>
  

          
          <div className="text-center mt-4">
            <Button 
              variant="secondary"
              type="submit" 
              className="me-2 save-btn" 
              disabled={loading}
            >
              Save
            </Button>
            <Button 
              variant="secondary" 
              onClick={handleCancel} 
              className="cancel-btn"
              disabled={loading}
            >
              Cancel
            </Button>
          </div>
        </Form>
      </Container>
      </div>
    </div>
  );
};

export default AddEditReportPage;




